import { Component, OnInit, HostListener } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { GreetingService, EmailTemplate, GreetingBroadcastRequest, GreetingCalendarLoaderRequest, LoaderAttributeDefinition } from 'src/app/service/greeting.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface Recipient {
  id: number; // CarIQ user id
  name: string;
  email: string;
  verified: boolean;
  firstName: string;
  lastName: string;
  userName: string;
  cellNumber: string;
  loaderValues?: { [key: string]: any };
  emailSubject?: string;
  emailText?: string | { [key: string]: any };
  notificationMessage?: string;
  scheduleDate?: string;
}

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
  standalone: false
})
export class NotificationPanelComponent implements OnInit {
  private readonly defaultNotificationType = 'Announcement';
  private readonly requiredLoaderFields = ['GreetigName', 'Email', 'NotificationType'];
  templates: EmailTemplate[] = [];
  selectedTemplate: EmailTemplate | null = null;
  loaderAttributes: LoaderAttributeDefinition[] = [];
  loaderDefinitionError = '';
  scheduleForLater = false;
  scheduleDate = this.getTomorrowDate();
  bulkUploadFile: File | null = null;
  bulkUploadRecipients: Recipient[] = [];
  bulkUploadError = '';
  recipients: Recipient[] = [];
  searchResults: Recipient[] = [];
  searchInput = '';
  showSearchResults = false;
  isSearching = false;

  // Template search properties
  templateSearchInput = '';
  showTemplateDropdown = false;
  filteredTemplates: EmailTemplate[] = [];

  private searchSubject = new Subject<string>();
  private templateSearchSubject = new Subject<string>();

  isLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(private greetingService: GreetingService) {}

  ngOnInit(): void {
    this.setupSearch();
    this.setupTemplateSearch();
    this.loadLoaderDefinition();
    this.loadTemplates();
  }

  loadLoaderDefinition(): void {
    this.loaderDefinitionError = '';
    this.greetingService.getGreetingBroadcasterLoaderDefinition().subscribe({
      next: (definition) => {
        this.loaderAttributes = (definition.attrDefinition || []).filter(attribute => !!attribute.name);
      },
      error: () => {
        this.loaderDefinitionError = 'Failed to load loader parameters.';
        this.loaderAttributes = [];
      }
    });
  }

  loadTemplates(): void {
    this.greetingService.getAllTemplates().subscribe({
      next: (data) => {
        this.templates = data;
        this.filteredTemplates = data;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load templates';
      }
    });
  }

  selectTemplate(template: EmailTemplate | null): void {
    this.selectedTemplate = template;
    this.templateSearchInput = template ? (template.name || template.emailTemplateName || 'Template #' + template.id) : '';
    this.showTemplateDropdown = false;
  }

  clearTemplateSelection(): void {
    this.selectedTemplate = null;
    this.templateSearchInput = '';
    this.showTemplateDropdown = false;
  }

  closeTemplateDropdown(): void {
    // Delay closing to allow click events on dropdown items
    setTimeout(() => {
      this.showTemplateDropdown = false;
    }, 150);
  }

  onScheduleDateChange(): void {
    // This method exists so Angular template binding stays valid.
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const templateSelector = target.closest('.template-selector');
    if (!templateSelector) {
      this.showTemplateDropdown = false;
    }
  }

  setupSearch(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((email: string) => {
        this.performEmailSearch(email);
      });
  }

  setupTemplateSearch(): void {
    this.templateSearchSubject
      .pipe(
        debounceTime(150),
        distinctUntilChanged()
      )
      .subscribe((searchTerm: string) => {
        this.filterTemplates(searchTerm);
      });
  }

  onTemplateSearchInput(searchTerm: string): void {
    this.templateSearchInput = searchTerm;
    this.templateSearchSubject.next(searchTerm);
    this.showTemplateDropdown = true;
  }

  filterTemplates(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredTemplates = this.templates;
    } else {
      const term = searchTerm.toLowerCase();
      this.filteredTemplates = this.templates.filter(template =>
        (template.name && template.name.toLowerCase().includes(term)) ||
        (template.emailTemplateName && template.emailTemplateName.toLowerCase().includes(term)) ||
        (template.emailSubject && template.emailSubject.toLowerCase().includes(term))
      );
    }
  }

  onSearchInput(email: string): void {
    this.searchInput = email;
    if (email.trim().length > 2) {
      this.searchSubject.next(email.trim());
      this.showSearchResults = true;
    } else {
      this.searchResults = [];
      this.showSearchResults = false;
    }
  }

  performEmailSearch(email: string): void {
    this.isSearching = true;
    this.errorMessage = '';

    this.greetingService.searchUsers(email, 1, 10).subscribe({
      next: (response) => {
        // Convert CarIqUser to Recipient format
        this.searchResults = response.rows
          .filter(user => !this.recipients.find(r => r.id === user.id))
          .map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`.trim(),
            email: user.email,
            verified: user.isActivated,
            firstName: user.firstName,
            lastName: user.lastName,
            userName: user.userName,
            cellNumber: user.cellNumber
          }));
        this.isSearching = false;
      },
      error: (error) => {
        this.searchResults = [];
        this.isSearching = false;
        // Don't show error for search failures, just clear results
      }
    });
  }

  addRecipient(user: Recipient): void {
    if (!this.recipients.find(r => r.id === user.id)) {
      this.recipients.push(user);
    }
    this.searchInput = '';
    this.searchResults = [];
    this.showSearchResults = false;
  }

  removeRecipient(recipientId: number): void {
    this.recipients = this.recipients.filter(r => r.id !== recipientId);
  }

  onBulkUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.bulkUploadError = '';
    this.bulkUploadRecipients = [];

    if (!file) {
      this.bulkUploadFile = null;
      return;
    }

    this.bulkUploadFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const recipients = this.parseBulkUploadFile(text);
        if (!recipients.length) {
          throw new Error('No recipients found in the uploaded file.');
        }
        this.bulkUploadRecipients = recipients;
        this.successMessage = `${file.name} is ready to upload.`;
        this.errorMessage = '';
        setTimeout(() => (this.successMessage = ''), 3000);
      } catch (error: any) {
        this.bulkUploadError = error?.message || 'Unable to parse bulk upload file.';
        this.bulkUploadFile = null;
        this.bulkUploadRecipients = [];
        input.value = '';
      }
    };
    reader.onerror = () => {
      this.bulkUploadFile = null;
      this.bulkUploadRecipients = [];
      this.bulkUploadError = 'Unable to read bulk upload file.';
      input.value = '';
    };
    reader.readAsText(file);
  }

  clearBulkUploadFile(input?: HTMLInputElement): void {
    this.bulkUploadFile = null;
    this.bulkUploadRecipients = [];
    this.bulkUploadError = '';
    if (input) {
      input.value = '';
    }
  }

  downloadTemplate(): void {
    const fields = this.scheduleForLater ? this.getScheduledCalendarLoaderFields() : this.getLoaderFieldNames();
    if (!fields.length) {
      this.errorMessage = 'Loader parameters are not available yet.';
      return;
    }

    const header = fields.join(',');
    const csv = this.scheduleForLater
      ? `${header}\n`
      : `${header}\n${fields.map(field => this.escapeCsvValue(this.getSampleValueForLoaderField(field))).join(',')}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bulk-upload-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  uploadBulkRecipients(input?: HTMLInputElement): void {
    if (!this.selectedTemplate) {
      this.errorMessage = 'Please select a VM template';
      return;
    }
    if (!this.scheduleForLater && !this.getLoaderFieldNames().length) {
      this.errorMessage = 'Loader parameters are not available yet.';
      return;
    }
    if (!this.bulkUploadRecipients.length) {
      this.errorMessage = 'Please upload a recipient file before submitting.';
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoading = true;

    if (this.scheduleForLater) {
      const requests = this.bulkUploadRecipients.map(recipient => this.buildScheduledCalendarLoaderRequest(recipient));
      this.greetingService.sendMultipleScheduledGreetings(requests).subscribe({
        next: () => {
          const uploadedCount = this.bulkUploadRecipients.length;
          this.successMessage = `Scheduled bulk calendar upload submitted for ${uploadedCount} row(s).`;
          this.clearBulkUploadFile(input);
          this.isLoading = false;
          setTimeout(() => (this.successMessage = ''), 3000);
        },
        error: (error) => {
          this.errorMessage = this.getScheduleErrorMessage(error);
          this.isLoading = false;
        }
      });
      return;
    }

    const requests = this.bulkUploadRecipients.map(recipient => this.buildSingleLoadRequest(recipient));
    this.greetingService.sendMultipleGreetings(requests).subscribe({
      next: () => {
        const uploadedCount = this.bulkUploadRecipients.length;
        this.successMessage = `Load Multiple submitted successfully for ${uploadedCount} user(s).`;
        this.clearBulkUploadFile(input);
        this.isLoading = false;
        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: (error) => {
        this.errorMessage = this.getSendErrorMessage(error);
        this.isLoading = false;
      }
    });
  }

  private parseBulkUploadFile(fileText: string): Recipient[] {
    const trimmed = fileText.trim();
    if (!trimmed) {
      throw new Error('Uploaded file is empty.');
    }

    if (this.scheduleForLater) {
      return this.parseScheduledBulkUploadFile(trimmed);
    }

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed);
      const rows = Array.isArray(parsed) ? parsed : (parsed?.rows || []);
      if (!Array.isArray(rows)) {
        throw new Error('JSON upload must be an array or contain a rows array.');
      }

      return rows
        .map((item: any, index: number) => ({
          id: index + 1,
          name: item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.userName || item.email || `Recipient ${index + 1}`,
          email: item.email || item.Email,
          verified: false,
          firstName: item.firstName || '',
          lastName: item.lastName || '',
          userName: item.userName || '',
          cellNumber: item.cellNumber || '',
          loaderValues: this.getLoaderValuesFromObject(item),
          emailSubject: item.emailSubject || '',
          emailText: item.emailText,
          notificationMessage: item.notificationMessage || '',
          scheduleDate: item.scheduleDate || item.scheduledDate || ''
        }))
        .filter(recipient => !!recipient.email);
    }

    const lines = trimmed.split(/\r?\n/).map(line => line.trim()).filter(line => line);
    const hasHeader = lines[0].toLowerCase().includes('email');
    const recipients: Recipient[] = [];

    if (hasHeader && lines.length > 1) {
      const headers = this.parseCsvRow(lines[0]).map(h => h.trim().toLowerCase());
      const originalHeaders = this.parseCsvRow(lines[0]).map(h => h.trim());
      const emailIndex = headers.findIndex(h => h === 'email' || h === 'e-mail');
      const nameIndex = headers.findIndex(h => h === 'name' || h === 'fullname' || h === 'full name');
      const emailSubjectIndex = headers.findIndex(h => h === 'emailsubject' || h === 'email subject');
      const emailTextIndex = headers.findIndex(h => h === 'emailtext' || h === 'email text');
      const notificationMessageIndex = headers.findIndex(h => h === 'notificationmessage' || h === 'notification message');
      const scheduleDateIndex = headers.findIndex(h => h === 'scheduledate' || h === 'scheduled date' || h === 'schedule date');

      if (emailIndex < 0) {
        throw new Error('CSV header must include an email column.');
      }

      for (let i = 1; i < lines.length; i += 1) {
        const columns = this.parseCsvRow(lines[i]).map(col => col.trim());
        const email = columns[emailIndex];
        if (email) {
          const loaderValues = this.getLoaderValuesFromColumns(originalHeaders, columns);
          recipients.push({
            id: recipients.length + 1,
            name: columns[nameIndex] || email,
            email,
            verified: false,
            firstName: '',
            lastName: '',
            userName: '',
            cellNumber: '',
            loaderValues,
            emailSubject: emailSubjectIndex >= 0 ? columns[emailSubjectIndex] : '',
            emailText: emailTextIndex >= 0 ? columns[emailTextIndex] : undefined,
            notificationMessage: notificationMessageIndex >= 0 ? columns[notificationMessageIndex] : '',
            scheduleDate: scheduleDateIndex >= 0 ? columns[scheduleDateIndex] : ''
          });
        }
      }
    } else {
      for (let i = 0; i < lines.length; i += 1) {
        recipients.push({
          id: i + 1,
          name: lines[i],
          email: lines[i],
          verified: false,
          firstName: '',
          lastName: '',
          userName: '',
          cellNumber: '',
          loaderValues: {
            Email: lines[i],
            GreetigName: this.getSelectedTemplateName(),
            NotificationType: this.defaultNotificationType
          }
        });
      }
    }

    return recipients.filter(r => !!r.email);
  }

  private parseScheduledBulkUploadFile(fileText: string): Recipient[] {
    const requiredFields = this.getScheduledCalendarLoaderFields();

    if (fileText.startsWith('{') || fileText.startsWith('[')) {
      const parsed = JSON.parse(fileText);
      const rows = Array.isArray(parsed) ? parsed : (parsed?.rows || []);
      if (!Array.isArray(rows)) {
        throw new Error('JSON upload must be an array or contain a rows array.');
      }

      const recipients = rows.map((item: any, index: number) => {
        this.validateScheduledRow(item, index + 1);

        return {
          id: index + 1,
          name: item.name,
          email: '',
          verified: false,
          firstName: '',
          lastName: '',
          userName: '',
          cellNumber: '',
          loaderValues: item,
          emailSubject: item.emailSubject,
          emailText: item.emailText,
          notificationMessage: item.notificationMessage,
          scheduleDate: item.scheduleDate
        };
      });

      return recipients;
    }

    const lines = fileText.split(/\r?\n/).map(line => line.trim()).filter(line => line);
    if (lines.length < 2) {
      throw new Error('CSV upload must include a header and at least one row.');
    }

    const headers = this.parseCsvRow(lines[0]).map(h => h.trim());
    const missingFields = requiredFields.filter(field => !headers.includes(field));
    const recipients: Recipient[] = [];

    if (missingFields.length) {
      throw new Error(`Scheduled CSV header must include ${missingFields.join(', ')}.`);
    }

    for (let i = 1; i < lines.length; i += 1) {
      const columns = this.parseCsvRow(lines[i]).map(col => col.trim());
      const loaderValues = this.getValuesFromExactColumns(headers, columns, requiredFields);
      this.validateScheduledRow(loaderValues, i);

      recipients.push({
        id: recipients.length + 1,
        name: loaderValues.name,
        email: '',
        verified: false,
        firstName: '',
        lastName: '',
        userName: '',
        cellNumber: '',
        loaderValues,
        emailSubject: loaderValues.emailSubject,
        emailText: loaderValues.emailText,
        notificationMessage: loaderValues.notificationMessage,
        scheduleDate: loaderValues.scheduleDate
      });
    }

    return recipients;
  }

  private getValuesFromExactColumns(headers: string[], columns: string[], fields: string[]): { [key: string]: string } {
    const values: { [key: string]: string } = {};
    fields.forEach(field => {
      values[field] = columns[headers.indexOf(field)] || '';
    });

    return values;
  }

  private validateScheduledRow(row: { [key: string]: any }, rowNumber: number): void {
    const missingFields = this.getScheduledCalendarLoaderFields().filter(field => row[field] == null || row[field] === '');
    if (missingFields.length) {
      throw new Error(`Scheduled row ${rowNumber} is missing ${missingFields.join(', ')}.`);
    }
  }

  validateForm(): boolean {
    if (!this.selectedTemplate) {
      this.errorMessage = 'Please select a VM template';
      return false;
    }
    if (!this.scheduleForLater && !this.getLoaderFieldNames().length) {
      this.errorMessage = 'Loader parameters are not available yet.';
      return false;
    }
    if (!this.scheduleForLater && this.recipients.length === 0) {
      this.errorMessage = 'Please add at least one recipient';
      return false;
    }
    if (this.scheduleForLater && !this.scheduleDate) {
      this.errorMessage = 'Please select a schedule date';
      return false;
    }
    if (this.scheduleForLater && this.scheduleDate <= this.getTodayDate()) {
      this.errorMessage = 'Please select a future schedule date';
      return false;
    }
    this.errorMessage = '';
    return true;
  }

  sendNotification(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;

    if (this.scheduleForLater) {
      this.greetingService.createTemplate(this.buildScheduledCalendarTemplate()).subscribe({
        next: () => {
          this.successMessage = `Notification scheduled for ${this.scheduleDate}.`;
          this.resetForm();
          this.isLoading = false;
          setTimeout(() => (this.successMessage = ''), 3000);
        },
        error: (error) => {
          this.errorMessage = this.getScheduleErrorMessage(error);
          this.isLoading = false;
        }
      });
      return;
    }

    const requests = this.recipients.map(recipient => this.buildSingleLoadRequest(recipient));

    forkJoin(requests.map(request => this.greetingService.sendSingleGreeting(request))).subscribe({
      next: (response) => {
        this.successMessage = `Load Single submitted successfully for ${this.recipients.length} user(s).`;
        this.resetForm();
        this.isLoading = false;
        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: (error) => {
        this.errorMessage = this.getSendErrorMessage(error);
        this.isLoading = false;
      }
    });
  }

  private buildSingleLoadRequest(recipient: Recipient): GreetingBroadcastRequest {
    const request: GreetingBroadcastRequest = {};

    this.getLoaderFieldNames().forEach(fieldName => {
      request[fieldName] = this.getLoaderFieldValue(fieldName, recipient);
    });

    request.GreetigName = request.GreetigName || this.getSelectedTemplateName();
    request.Email = request.Email || recipient.email;
    request.NotificationType = request.NotificationType || this.defaultNotificationType;

    return request;
  }

  private buildScheduledCalendarTemplate(): EmailTemplate {
    const template = this.selectedTemplate!;
    return {
      name: template.name || template.emailTemplateName || this.templateSearchInput,
      emailSubject: template.emailSubject || '',
      emailText: this.normalizeEmailText(template.emailText) || {},
      notificationMessage: template.notificationMessage || '',
      emailTemplateName: template.emailTemplateName || '',
      imageUrl: template.imageUrl || '',
      itsDayDate: this.scheduleDate,
      scheduleDate: this.scheduleDate
    };
  }

  private buildScheduledCalendarLoaderRequest(recipient: Recipient): GreetingCalendarLoaderRequest {
    const loaderValues = recipient.loaderValues || {};

    return {
      name: loaderValues.name,
      emailSubject: loaderValues.emailSubject,
      emailText: loaderValues.emailText,
      notificationMessage: loaderValues.notificationMessage,
      emailTemplateName: loaderValues.emailTemplateName,
      imageUrl: loaderValues.imageUrl,
      scheduleDate: loaderValues.scheduleDate
    };
  }

  private getSendErrorMessage(error: any): string {
    if (error?.status === 403) {
      return 'Loader failed: authorization is required for GreetingsAndInformationBroadcaster.';
    }

    if (error?.status === 400) {
      return 'Loader failed: request body is invalid. Check GreetigName and Email.';
    }

    if (error?.status === 0) {
      return 'Send failed: browser could not reach the API. Check CORS/preflight and network access.';
    }

    if (error?.status >= 500) {
      return 'Loader failed on the server. Check broadcaster loader logs.';
    }

    return 'Failed to send notification. Please try again.';
  }

  private getScheduleErrorMessage(error: any): string {
    if (error?.status === 403) {
      return 'Schedule failed: authorization is required for the calendar API.';
    }

    if (error?.status === 400) {
      return 'Schedule failed: calendar payload is invalid. Check template name, subject, file, and date.';
    }

    if (error?.status === 0) {
      return 'Schedule failed: browser could not reach the API. Check CORS/preflight and network access.';
    }

    if (error?.status >= 500) {
      return 'Schedule failed on the server. Check calendar API logs.';
    }

    return 'Failed to schedule notification. Please try again.';
  }

  resetForm(): void {
    this.selectedTemplate = null;
    this.scheduleForLater = false;
    this.scheduleDate = this.getTomorrowDate();
    this.bulkUploadFile = null;
    this.bulkUploadRecipients = [];
    this.recipients = [];
    this.searchInput = '';
    this.searchResults = [];
  }

  closeSearchResults(): void {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  getSelectedTemplateName(): string {
    return this.selectedTemplate?.name || '';
  }

  getSingleLoadPreview(): string {
    if (this.scheduleForLater && this.bulkUploadRecipients.length) {
      return JSON.stringify(this.bulkUploadRecipients.map(recipient => this.buildScheduledCalendarLoaderRequest(recipient)), null, 2);
    }

    if (this.scheduleForLater && this.selectedTemplate) {
      return JSON.stringify(this.buildScheduledCalendarTemplate(), null, 2);
    }

    const recipient = this.bulkUploadRecipients.length ? this.bulkUploadRecipients[0] : this.recipients[0];
    const preview: GreetingBroadcastRequest = {};

    this.getLoaderFieldNames().forEach(fieldName => {
      preview[fieldName] = recipient ? this.getLoaderFieldValue(fieldName, recipient) : this.getDefaultLoaderFieldValue(fieldName);
    });

    return JSON.stringify(preview, null, 2);
  }

  getBulkUploadFieldLabel(field: string): string {
    const attribute = this.loaderAttributes.find(item => item.name === field);
    return attribute?.description ? `${field} - ${attribute.description}` : field;
  }

  getLoaderFieldNames(): string[] {
    const fields = this.loaderAttributes.map(attribute => attribute.name);
    this.requiredLoaderFields.forEach(field => {
      if (!fields.some(loaderField => loaderField.toLowerCase() === field.toLowerCase())) {
        fields.push(field);
      }
    });

    return fields;
  }

  getBulkUploadHint(): string {
    const fields = this.scheduleForLater ? this.getScheduledCalendarLoaderFields() : this.getLoaderFieldNames();
    if (!fields.length) {
      return 'Loader parameters are loading.';
    }

    return `CSV must include ${fields.join(', ')}.`;
  }

  private getScheduledCalendarLoaderFields(): string[] {
    return ['name', 'emailSubject', 'emailText', 'notificationMessage', 'emailTemplateName', 'imageUrl', 'scheduleDate'];
  }

  private getLoaderFieldValue(fieldName: string, recipient: Recipient): any {
    const loaderValues = recipient.loaderValues || {};
    const loaderValueKey = Object.keys(loaderValues).find(key => key.toLowerCase() === fieldName.toLowerCase());
    if (loaderValueKey && loaderValues[loaderValueKey] !== '') {
      return loaderValues[loaderValueKey];
    }

    return this.getDefaultLoaderFieldValue(fieldName, recipient);
  }

  private getDefaultLoaderFieldValue(fieldName: string, recipient?: Recipient): any {
    const normalizedField = fieldName.toLowerCase();

    if (normalizedField === 'email' || normalizedField === 'e-mail') {
      return recipient?.email || '';
    }

    if (normalizedField === 'greetigname' || normalizedField === 'greetingname') {
      return this.getSelectedTemplateName();
    }

    if (normalizedField === 'notificationtype') {
      return this.defaultNotificationType;
    }

    if (normalizedField === 'emailsubject') {
      return recipient?.emailSubject || this.selectedTemplate?.emailSubject || '';
    }

    if (normalizedField === 'emailtext') {
      return this.normalizeEmailText(recipient?.emailText ?? this.selectedTemplate?.emailText) || '';
    }

    if (normalizedField === 'notificationmessage') {
      return recipient?.notificationMessage || this.selectedTemplate?.notificationMessage || '';
    }

    if (normalizedField === 'scheduleddate' || normalizedField === 'scheduledate') {
      return recipient?.scheduleDate || (this.scheduleForLater ? this.scheduleDate : '');
    }

    return '';
  }

  private getSampleValueForLoaderField(fieldName: string): string {
    const value = this.getDefaultLoaderFieldValue(fieldName);
    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value || '';
  }

  private getLoaderValuesFromObject(item: any): { [key: string]: any } {
    const values: { [key: string]: any } = {};
    this.getLoaderFieldNames().forEach(fieldName => {
      const objectKey = Object.keys(item).find(key => key.toLowerCase() === fieldName.toLowerCase());
      values[fieldName] = objectKey ? item[objectKey] : this.getDefaultLoaderFieldValue(fieldName, {
        id: 0,
        name: item.name || item.email || '',
        email: item.email || item.Email || '',
        verified: false,
        firstName: item.firstName || '',
        lastName: item.lastName || '',
        userName: item.userName || '',
        cellNumber: item.cellNumber || ''
      });
    });

    return values;
  }

  private getLoaderValuesFromColumns(headers: string[], columns: string[]): { [key: string]: any } {
    const values: { [key: string]: any } = {};
    this.getLoaderFieldNames().forEach(fieldName => {
      const index = headers.findIndex(header => header.toLowerCase() === fieldName.toLowerCase());
      values[fieldName] = index >= 0 ? columns[index] : this.getDefaultLoaderFieldValue(fieldName);
    });

    return values;
  }

  private escapeCsvValue(value: string): string {
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
  }

  private parseCsvRow(row: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i += 1) {
      const char = row[i];
      const nextChar = row[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current);
    return values;
  }

  private normalizeEmailText(value?: string | { [key: string]: any }): string | { [key: string]: any } | undefined {
    if (value == null) {
      return undefined;
    }

    if (typeof value === 'object') {
      return value;
    }

    const trimmed = value.trim();
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed !== '{}' && trimmed !== '{ }') {
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }

    return value;
  }

  openDatePicker(dateInput: HTMLInputElement): void {
    dateInput.focus();
    const inputWithPicker = dateInput as HTMLInputElement & { showPicker?: () => void };
    inputWithPicker.showPicker?.();
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, '0');
    const day = `${today.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getTomorrowDate(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = `${tomorrow.getMonth() + 1}`.padStart(2, '0');
    const day = `${tomorrow.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
